/**
 * Profile data layer (Phase 1 #5).
 *
 * Pairs the typed `api` client with the central `queryKeys` factory. The full
 * profile is fetched by username; the friends / media / photos / blogs lists are
 * fetched lazily per tab (media is infinite). `useUserAction` drives the friend
 * state machine + poke / block / report, patching the cached profile on success.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  Activity,
  BlogSummary,
  Paginated,
  Photo,
  User,
  UserDetails,
  UserMediaItem,
} from '@/types/api';

/** Serialise params into a `?key=value` query string (skips empty values). */
function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/* -------------------------------------------------------------------------- */
/* Profile                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Full profile for a user, looked up by username (`GET /user/details/{username}
 * ?by=username`). The route `:username` param is treated as the username.
 */
export function useProfile(username: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.details(username ?? ''),
    enabled: !!username,
    // Per-profile one-shot — long sessions can visit dozens of
    // profiles. 1 min retention still warms the back-button path
    // but lets the long-tail data drop out of memory.
    gcTime: 60_000,
    queryFn: () =>
      api.get<UserDetails>(
        `user/details/${encodeURIComponent(username ?? '')}?by=username&image_width=600`,
      ),
  });
}

/* -------------------------------------------------------------------------- */
/* Friends                                                                     */
/* -------------------------------------------------------------------------- */

/** A user's friends list (`GET /user/friends/{id}`), optionally searched.
 *  `enabled` defaults to true; callers can pass `false` to skip the network
 *  call when the consumer is conditionally mounted (e.g. the rail
 *  `MiniFriends` only fires on the /friends tab to avoid hitting the API
 *  on every profile tab visit). */
export function useUserFriends(userId: number | undefined, q = '', enabled = true) {
  const params = { limit: 24, q: q.trim() };
  return useQuery({
    queryKey: queryKeys.users.friends(userId ?? 0, params),
    enabled: !!userId && enabled,
    queryFn: () =>
      api.get<Paginated<User>>(`user/friends/${userId}${toQuery(params)}`),
  });
}

const FRIENDS_LIMIT = 24;
type FriendsPage = Paginated<User>;
type FriendsInfinite = InfiniteData<FriendsPage, number>;

/**
 * Infinite friends list for the dashboard Friends page — fetches the next
 * page when the user scrolls to the bottom. The same `GET /user/friends/{id}`
 * endpoint, but page-aware. The `q` search param resets pagination on change
 * because it's part of the query key.
 */
export function useUserFriendsInfinite(
  userId: number | undefined,
  q = '',
) {
  const params = { limit: FRIENDS_LIMIT, q: q.trim() };
  return useInfiniteQuery<FriendsPage, Error, FriendsInfinite, QueryKey, number>({
    queryKey: queryKeys.users.friendsInfinite(userId ?? 0, params),
    enabled: !!userId,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<FriendsPage>(
        `user/friends/${userId}${toQuery({ ...params, page: pageParam })}`,
      ),
    getNextPageParam: (lastPage, _all, lastParam) => {
      if (lastPage.next === false) return undefined;
      const got = lastPage.list?.length ?? 0;
      if (got === 0 || got < FRIENDS_LIMIT) return undefined;
      return lastParam + 1;
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Media (infinite)                                                            */
/* -------------------------------------------------------------------------- */

const MEDIA_LIMIT = 24;

type MediaPage = Paginated<UserMediaItem>;
type MediaInfinite = InfiniteData<MediaPage, number>;

/** Infinite media grid for a user (`GET /user/media/{id}`). */
export function useUserMedia(userId: number | undefined) {
  return useInfiniteQuery<MediaPage, Error, MediaInfinite, QueryKey, number>({
    queryKey: queryKeys.users.media(userId ?? 0, { limit: MEDIA_LIMIT }),
    enabled: !!userId,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<MediaPage>(
        `user/media/${userId}${toQuery({ page: pageParam, limit: MEDIA_LIMIT, width: 400, height: 400 })}`,
      ),
    getNextPageParam: (lastPage, _all, lastParam) => {
      if (lastPage.next === false) return undefined;
      const got = lastPage.list?.length ?? 0;
      if (got === 0 || got < MEDIA_LIMIT) return undefined;
      return lastParam + 1;
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Photos                                                                      */
/* -------------------------------------------------------------------------- */

/** A user's profile photos (`GET /user/photos/get/{id}`). */
export function useUserPhotos(userId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.users.photos(userId ?? 0),
    enabled: !!userId && enabled,
    queryFn: () =>
      api.get<{ list: Photo[] }>(`user/photos/get/${userId}?image_width=600`),
  });
}

/* -------------------------------------------------------------------------- */
/* Blogs                                                                       */
/* -------------------------------------------------------------------------- */

/** A user's blogs (`GET /blogs/list?user_id={id}`). */
export function useUserBlogs(userId: number | undefined) {
  const params = { user_id: userId, limit: 24 };
  return useQuery({
    queryKey: queryKeys.users.blogs(userId ?? 0, params),
    enabled: !!userId,
    queryFn: () =>
      api.get<Paginated<BlogSummary>>(`blogs/list${toQuery(params)}`),
  });
}

/* -------------------------------------------------------------------------- */
/* Single activity (deep link)                                                 */
/* -------------------------------------------------------------------------- */

/** A single activity by id (`GET /timeline/get/{id}`) for deep-link views.
 *  Backend wraps the row in `{ list: [activity], next: false, ... }` —
 *  `TimelineController.getItem` calls `Activity.getTimeline` which always
 *  returns the timeline-page shape. Unwrap to the first item here so
 *  callers can just use `data` (or `null` for missing posts). */
export function useActivity(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.timeline.detail(id ?? 0),
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ list?: Activity[] }>(
        `timeline/get/${id}?width=600`,
      );
      return res?.list?.[0] ?? null;
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Social actions                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The social actions performed on a profile. `addfriend` either sends a request
 * (friendType 0) or accepts a pending one (friendType 2); the rest mirror the
 * legacy `user/action/{action}/{id}` contract.
 */
export type UserAction =
  | 'addfriend'
  | 'cancelrequest'
  | 'acceptrequest'
  | 'rejectrequest'
  | 'removefriend'
  | 'unfriend'
  | 'poke'
  | 'cancelpoke'
  | 'block'
  | 'unblock'
  | 'report';

/** Loosely-typed response for `POST /user/action/{action}/{id}`. */
export interface UserActionResult {
  msg?: string;
  friends?: number;
  requests?: number;
  pokes?: number;
  allowChat?: boolean;
}

export interface UserActionVars {
  action: UserAction;
  /** Target user id. */
  id: number;
  /** Username — used to patch the cached profile by its query key. */
  username?: string;
  /** Report reason code (only for `report`). */
  reason?: number;
}

/**
 * Map an action to the next `friendType`, or `null` when the action does not
 * change the relationship. `addfriend` from a *received* request (friendType 2)
 * becomes friends (3); from no relationship (0) it becomes a sent request (1).
 */
function nextFriendType(
  action: UserAction,
  current: number | undefined,
): number | null {
  switch (action) {
    case 'addfriend':
      return current === 2 ? 3 : 1;
    case 'acceptrequest':
      return 3;
    case 'cancelrequest':
    case 'rejectrequest':
      return 0;
    case 'removefriend':
    case 'unfriend':
      return 0;
    default:
      return null;
  }
}

/**
 * Perform a social action and patch the cached profile so the header reflects
 * the new `friendType` / `isBlocked` / `total.friends` immediately. The mutation
 * resolves with the raw action result so callers can toast `msg`.
 */
export function useUserAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, id, reason }: UserActionVars) =>
      api.post<UserActionResult | string>(
        `user/action/${action}/${id}`,
        action === 'report' ? { reason } : undefined,
      ),
    onSuccess: (data, vars) => {
      if (!vars.username) return;
      const key = queryKeys.users.details(vars.username);
      const prev = qc.getQueryData<UserDetails>(key);
      if (!prev) return;

      const result = typeof data === 'string' ? {} : (data ?? {});
      const ft = nextFriendType(vars.action, prev.friendType);

      const patched: UserDetails = { ...prev };
      if (ft !== null) patched.friendType = ft;
      if (vars.action === 'block') patched.isBlocked = true;
      if (vars.action === 'unblock') patched.isBlocked = false;
      // NOTE: `result.friends` is the AUTHENTICATED USER's friend count,
      // not the target's — verified in nodejs_server controllers
      // (unfriend / addfriend-accept / acceptrequest). Writing it into
      // the target's cached profile produced garbled counts. We instead
      // just invalidate the target so the next read re-fetches their
      // true total.
      if (typeof result.allowChat === 'boolean') {
        patched.settings = { ...prev.settings, allow_chat: result.allowChat };
      }
      qc.setQueryData<UserDetails>(key, patched);
      if (typeof result.friends === 'number') {
        qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}
